import { store } from '@chilipiper/utils'
import { readCookies } from './cookies'
import { logger } from './logger'

/**
 * Ad-platform click-id capture (CDP-131).
 *
 * Every ad platform appends a click id to its ad-click landing URLs. It identifies the click, not
 * the person, and it is the deterministic match key for the platforms' conversion APIs — a click id
 * not captured at click time is attribution permanently lost. We capture the ids onto every jitsu
 * event's `context.clientIds` (flattened to `context_client_ids_*` in the warehouse):
 *
 * - URL param is the primary source: li_fat_id (LinkedIn), gclid / gbraid / wbraid (Google), fbclid
 *   (Meta).
 * - The platform tag's cookie is the fallback: li_fat_id (Insight Tag), _gcl_aw (gtag), _fbc + _fbp
 *   (Meta Pixel).
 * - Captured values are persisted so events fired after the landing page (form fills, bookings) still
 *   carry them, even when the platform tag is blocked and its cookie never appears.
 * - GA4 client/session ids are read from the _ga / _ga_* cookies.
 */

const CLICK_IDS_STORE_KEY = 'chilitracking:click-ids'

const CLICK_ID_PARAMS = ['li_fat_id', 'gclid', 'gbraid', 'wbraid', 'fbclid'] as const

type ClickIdParam = (typeof CLICK_ID_PARAMS)[number]

const DAY_MS = 24 * 60 * 60 * 1000

// Persisted values expire together with the platform cookie they substitute for, so our fallback
// never attributes further back than the platform itself would (Insight Tag li_fat_id: 30 days;
// gtag _gcl_aw and Meta Pixel _fbc: 90 days).
const CLICK_ID_EXPIRY_MS: Record<ClickIdParam, number> = {
  li_fat_id: 30 * DAY_MS,
  gclid: 90 * DAY_MS,
  gbraid: 90 * DAY_MS,
  wbraid: 90 * DAY_MS,
  fbclid: 90 * DAY_MS,
}

type CapturedClickId = { value: string; capturedAt: number }
type StoredClickIds = Partial<Record<ClickIdParam, CapturedClickId>>

type Ga4ClientIds = {
  clientId?: string
  sessionIds?: Record<string, string>
}

type ClientIds = {
  li_fat_id?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  fbc?: string
  fbp?: string
  ga4?: Ga4ClientIds
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isCapturedClickId = (value: unknown): value is CapturedClickId =>
  isRecord(value) && typeof value.value === 'string' && typeof value.capturedAt === 'number'

const readStoredClickIds = (): StoredClickIds => {
  const raw: unknown = store.get(CLICK_IDS_STORE_KEY)
  if (!isRecord(raw)) return {}

  const now = Date.now()
  const stored: StoredClickIds = {}
  for (const param of CLICK_ID_PARAMS) {
    const entry = raw[param]
    if (isCapturedClickId(entry) && now - entry.capturedAt < CLICK_ID_EXPIRY_MS[param]) {
      stored[param] = entry
    }
  }
  return stored
}

const readUrlClickIds = (): Partial<Record<ClickIdParam, string>> => {
  const searchParams = new URLSearchParams(window.location.search)
  const urlIds: Partial<Record<ClickIdParam, string>> = {}
  for (const param of CLICK_ID_PARAMS) {
    const value = searchParams.get(param)
    if (value) {
      urlIds[param] = value
    }
  }
  return urlIds
}

/**
 * Merges the click ids present on the current URL into the persisted set. A value already persisted
 * keeps its original capturedAt so derived values (fbc) and expiry stay stable across re-landings
 * with the same id.
 */
const captureFromUrl = (
  stored: StoredClickIds,
  urlIds: Partial<Record<ClickIdParam, string>>
): StoredClickIds => {
  let changed = false
  const merged: StoredClickIds = { ...stored }

  for (const param of CLICK_ID_PARAMS) {
    const value = urlIds[param]
    if (value && merged[param]?.value !== value) {
      merged[param] = { value, capturedAt: Date.now() }
      changed = true
    }
  }

  if (changed) {
    store.set(CLICK_IDS_STORE_KEY, merged)
  }
  return merged
}

// _gcl_aw holds `GCL.<timestamp>.<gclid>` — the click id is everything after the second dot.
const parseGclAwCookie = (value: string | undefined): string | undefined => {
  if (!value) return undefined
  const parts = value.split('.')
  if (parts.length < 3) return undefined
  return parts.slice(2).join('.') || undefined
}

// _ga_<CONTAINER> session cookies come in two formats: the legacy dotted one
// (`GS1.1.<session id>. ...`) and the newer `$`-delimited one (`GS2.1.s<session id>$o5$...`).
// Parsing mirrors jitsu-js's parseGa4SessionId so our values match what the snippet would produce.
const parseGa4SessionId = (cookieValue: string): string | undefined => {
  if (cookieValue.startsWith('GA1') || cookieValue.startsWith('GS1')) {
    return cookieValue.split('.')[2]
  }
  const match = cookieValue.match(/^GS\d+\.\d+\.(?:[\w_-]+[$])*s(\d+)(?:$|[$])/)
  return match ? match[1] : undefined
}

// _ga holds `GA1.<n>.<random>.<timestamp>` — the GA4 client id is the last two segments.
const parseGa4ClientIds = (cookies: Record<string, string>): Ga4ClientIds | undefined => {
  const ga4: Ga4ClientIds = {}

  const gaCookie = cookies['_ga']
  if (gaCookie) {
    const parts = gaCookie.split('.')
    if (parts.length >= 4) {
      ga4.clientId = parts.slice(-2).join('.')
    }
  }

  const sessionIds: Record<string, string> = {}
  for (const [name, value] of Object.entries(cookies)) {
    if (!name.startsWith('_ga_')) continue
    const sessionId = parseGa4SessionId(value)
    if (sessionId) {
      sessionIds[name.slice('_ga_'.length)] = sessionId
    }
  }
  if (Object.keys(sessionIds).length > 0) {
    ga4.sessionIds = sessionIds
  }

  return ga4.clientId || ga4.sessionIds ? ga4 : undefined
}

// Meta's fbc format: `fb.<subdomain index>.<capture time ms>.<fbclid>` — the same shape the Pixel
// writes to _fbc, so a synthesized value is a valid Conversions API match key.
const buildFbcFromFbclid = (fbclid: CapturedClickId): string =>
  `fb.1.${fbclid.capturedAt}.${fbclid.value}`

/**
 * Assembles `context.clientIds` from the three sources. Per click id: the URL param on the current
 * page wins (a fresh click must never be shadowed by a previous one), then the platform cookie,
 * then our persisted capture as the last resort for when the platform tag is blocked.
 */
const collectClientIds = (): ClientIds => {
  const urlIds = readUrlClickIds()
  const stored = captureFromUrl(readStoredClickIds(), urlIds)
  const cookies = readCookies()

  const clientIds: ClientIds = {}

  const liFatId = urlIds.li_fat_id ?? cookies['li_fat_id'] ?? stored.li_fat_id?.value
  if (liFatId) clientIds.li_fat_id = liFatId

  const gclid = urlIds.gclid ?? parseGclAwCookie(cookies['_gcl_aw']) ?? stored.gclid?.value
  if (gclid) clientIds.gclid = gclid

  const gbraid = urlIds.gbraid ?? stored.gbraid?.value
  if (gbraid) clientIds.gbraid = gbraid

  const wbraid = urlIds.wbraid ?? stored.wbraid?.value
  if (wbraid) clientIds.wbraid = wbraid

  // A fbclid on the current URL is a fresh click — synthesize fbc from it rather than trusting
  // _fbc, which the Pixel may not have updated yet at event time.
  const storedFbclid = stored.fbclid
  const fbc =
    urlIds.fbclid && storedFbclid
      ? buildFbcFromFbclid(storedFbclid)
      : (cookies['_fbc'] ?? (storedFbclid ? buildFbcFromFbclid(storedFbclid) : undefined))
  if (fbc) clientIds.fbc = fbc

  const fbp = cookies['_fbp']
  if (fbp) clientIds.fbp = fbp

  const ga4 = parseGa4ClientIds(cookies)
  if (ga4) clientIds.ga4 = ga4

  return clientIds
}

/**
 * Re-captures and re-attaches `context.clientIds` on the jitsu context. Called before every
 * outgoing event so ids appearing after init (a cookie the platform tag sets late, a new click
 * landing mid-session) are picked up. Never throws — the tracker runs on customer sites.
 */
export const refreshClientIds = (): void => {
  if (!window.jitsu) return
  try {
    const clientIds = collectClientIds()
    if (Object.keys(clientIds).length === 0) return
    window.jitsu.setContextProperty('clientIds', clientIds)
  } catch (error) {
    logger.warn('Failed to refresh clientIds', error)
  }
}
